import { useEffect, useState } from 'react';
import { pollForWindowProperty } from '@cx-web/utils/pollForWindowObject';

import Icon from '../../../../primitives/icon/index';

import { getOcWindow } from './ocWindow';
import styles from './UKBetslip.module.css';

type UKBetslipProps = {
  apiKey: string;
  betslipScriptUrl: string;
  deviceId: string;
  oddsFormat: string;
  repub: string;
};

export const UKBetslip = ({ apiKey, betslipScriptUrl, deviceId, oddsFormat, repub }: UKBetslipProps) => {
  const [betCount, setBetCount] = useState(0);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const ocWindow = getOcWindow();

    ocWindow.oc = ocWindow.oc ?? { betslipAdvertActive: false };
    ocWindow.oc.betslipAdvertActive = false;

    let xpId = '';
    if (typeof ocWindow.xtremepush !== 'undefined') {
      const xpDeviceInfo = ocWindow.xtremepush('get', 'device_info') as { id?: string } | undefined;
      if (typeof xpDeviceInfo !== 'undefined') {
        xpId = xpDeviceInfo.id ?? '';
      }
    }

    const scriptEl = document.createElement('script');
    scriptEl.src = betslipScriptUrl;
    scriptEl.type = 'text/javascript';
    scriptEl.async = true;
    document.head.appendChild(scriptEl);

    const loadBetslip = async () => {
      try {
        await pollForWindowProperty('ocRenderers.integrated-betslip');
      } catch {
        console.warn('OC Betslip renderer not present');
        if (!cancelled) setLoadState('failed');
        return;
      }
      if (cancelled) return;

      ocWindow.ocRenderers['integrated-betslip']({ apiKey, deviceId, oddsFormat, repub, xpId });

      // Wait for getBetCount — a partial { setCallbacks } ocBetslip can appear first.
      try {
        await pollForWindowProperty('ocBetslip.getBetCount');
      } catch {
        console.warn('OC Betslip not loaded');
        if (!cancelled) setLoadState('failed');
        return;
      }
      if (cancelled) return;

      const betslip = ocWindow.ocBetslip;
      // CDN calls setBetCount with no args; always read getBetCount().
      betslip.setCallbacks({
        setBetCount: () => setBetCount(betslip.getBetCount()),
      });
      setBetCount(betslip.getBetCount());

      let lastBetCount = betslip.getBetCount();
      intervalId = setInterval(() => {
        const currentCount = betslip.getBetCount();
        if (currentCount === lastBetCount) return;
        setBetCount(currentCount);
        lastBetCount = currentCount;
      }, 1000);

      setLoadState('ready');
    };

    loadBetslip();

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      scriptEl.remove();
    };
  }, [apiKey, betslipScriptUrl, deviceId, oddsFormat, repub]);

  if (loadState === 'failed') return null;

  const isReady = loadState === 'ready';
  const accessibleName = betCount > 0 ? `Betslip, ${betCount} ${betCount === 1 ? 'bet' : 'bets'}` : 'Betslip';

  return (
    <button
      aria-busy={!isReady}
      aria-label={accessibleName}
      className={styles.wrapper}
      data-testid="uk-betslip-button"
      disabled={!isReady}
      onClick={isReady ? () => getOcWindow().ocBetslip.toggle() : undefined}
      type="button"
    >
      <Icon height={28} icon="BetslipUk" width={28} />
      <span aria-hidden="true" className={styles.betCount} data-testid="uk-betslip-count">
        {betCount}
      </span>
    </button>
  );
};
